/**
 * External Payments API Client
 * Handles requests to external payments endpoints (onboarding, transactions, match actions)
 */

import { z } from 'zod';
import {
  TransactionMatchResponseSchema,
  BankTransactionResponseSchema,
  CandidateTransactionResponseSchema,
  OnboardingTenantResponseSchema,
  TransactionListQuerySchema,
} from '@resida/api-types/payments';

const API_BASE = '/landlord/api/manager';

// Derived from TransactionMatchResponseSchema in @resida/api-types/payments
export type TransactionMatch = z.infer<typeof TransactionMatchResponseSchema>;

// TODO(api-types-922): matchStatus here is missing 'platform_payment' — a real status the
// server actively sets (apps/api/src/services/payments/bank-transaction.service.ts writes
// `matchStatus: 'platform_payment'`, and components/MatchStatusBadge.tsx already renders it) —
// but BankTransactionResponseSchema's enum (@resida/api-types/payments) doesn't include it, and
// does include 'excluded'/'ignored' which the old hand-written type here didn't. Deriving
// BankTransaction from that schema surfaces this pre-existing enum drift as a real type
// mismatch rather than papering over it — see the matching TODO in
// packages/api-types/src/payments/external-payments.ts. Needs a human decision on whether to
// widen the schema's enum.
// Derived from BankTransactionResponseSchema in @resida/api-types/payments
export type BankTransaction = z.infer<typeof BankTransactionResponseSchema>;

export interface BankTransactionListResponse {
  transactions: BankTransaction[];
}

// Derived from CandidateTransactionResponseSchema in @resida/api-types/payments
export type CandidateTransaction = z.infer<typeof CandidateTransactionResponseSchema>;

// Derived from OnboardingTenantResponseSchema in @resida/api-types/payments
export type OnboardingTenant = z.infer<typeof OnboardingTenantResponseSchema>;

export interface OnboardingDataResponse {
  tenants: OnboardingTenant[];
}

export interface OnboardingMatch {
  bankTransactionId: string;
  tenantId: string;
  leaseId: string;
}

export interface SubmitOnboardingMatchesRequest {
  linkedBankAccountId: string;
  matches: OnboardingMatch[];
}

// Derived from TransactionListQuerySchema in @resida/api-types/payments
export type TransactionListQuery = z.infer<typeof TransactionListQuerySchema>;

export async function fetchOnboardingData(propertyId: string): Promise<OnboardingDataResponse> {
  const response = await fetch(
    `${API_BASE}/properties/${propertyId}/external-payments/onboarding`,
    { credentials: 'include' }
  );
  if (!response.ok) throw new Error(`Failed to fetch onboarding data: ${response.status}`);
  return response.json();
}

export async function submitOnboardingMatches(
  propertyId: string,
  data: SubmitOnboardingMatchesRequest
): Promise<{ processed: number }> {
  const response = await fetch(
    `${API_BASE}/properties/${propertyId}/external-payments/onboarding`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) throw new Error(`Failed to submit onboarding matches: ${response.status}`);
  return response.json();
}

export async function fetchTransactions(
  propertyId: string,
  query?: TransactionListQuery
): Promise<BankTransactionListResponse> {
  const params = new URLSearchParams();
  if (query?.matchStatus) params.set('matchStatus', query.matchStatus);
  if (query?.direction) params.set('direction', query.direction);
  if (query?.dateFrom) params.set('dateFrom', query.dateFrom);
  if (query?.dateTo) params.set('dateTo', query.dateTo);

  const qs = params.toString();
  const url = `${API_BASE}/properties/${propertyId}/external-payments/transactions${qs ? `?${qs}` : ''}`;

  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) throw new Error(`Failed to fetch transactions: ${response.status}`);
  return response.json();
}

export async function confirmTransactionMatch(id: string): Promise<void> {
  const response = await fetch(
    `${API_BASE}/external-payments/transactions/${id}/confirm`,
    { method: 'POST', credentials: 'include' }
  );
  if (!response.ok) throw new Error(`Failed to confirm match: ${response.status}`);
}

export async function dismissTransaction(id: string): Promise<void> {
  const response = await fetch(
    `${API_BASE}/external-payments/transactions/${id}/dismiss`,
    { method: 'POST', credentials: 'include' }
  );
  if (!response.ok) throw new Error(`Failed to dismiss transaction: ${response.status}`);
}

export async function manualMatchTransaction(
  id: string,
  data: { leaseId: string; tenantId: string }
): Promise<void> {
  const response = await fetch(
    `${API_BASE}/external-payments/transactions/${id}/match`,
    {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    }
  );
  if (!response.ok) throw new Error(`Failed to manually match transaction: ${response.status}`);
}
